import useIsMobile from '../hooks/useIsMobile';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsAPI } from '../services/api';
import toast from 'react-hot-toast';

const A = {
  eyebrow: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.8px', color: '#e97117', marginBottom: '6px' },
  h1: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', lineHeight: 0.95, letterSpacing: '-0.02em', margin: 0, color: '#14562e' },
  subtitle: { color: '#6a7268', margin: '6px 0 0', fontSize: '15px' },
  list: { display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '20px' },
  card: {
    background: '#fff', border: '1px solid #e5ddc9', borderRadius: '16px',
    padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px',
    transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.3s',
  },
  bar: { width: '4px', borderRadius: '4px', alignSelf: 'stretch', minHeight: '40px', flexShrink: 0 },
  info: { flex: 1, minWidth: 0 },
  title: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: '16px', color: '#14562e', margin: 0 },
  desc: { fontSize: '13.5px', color: '#6a7268', marginTop: '3px' },
  right: { display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 },
  vehicleTag: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '13px', fontWeight: 600, color: '#6a7268',
  },
  btn: {
    padding: '10px 20px', borderRadius: '10px', fontWeight: 700, fontSize: '13px',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
    whiteSpace: 'nowrap', border: 'none',
  },
  btnPrimary: { background: '#14562e', color: '#fff', border: 'none' },
  btnOutline: { background: 'transparent', color: '#14562e', border: '1.5px solid #e5ddc9' },
  empty: { textAlign: 'center', padding: '60px 20px' },
  tab: {
    padding: '10px 20px', borderRadius: '10px', fontWeight: 700, fontSize: '13px',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
    border: '1.5px solid #e5ddc9', background: 'transparent', color: '#6a7268',
  },
  tabActive: {
    background: '#14562e', color: '#fff', border: '1.5px solid #14562e',
  },
};

function urgencyColor(alert) {
  if (alert.priority === 'high' || alert.urgency === 'urgent') return '#c94a2f';
  if (alert.priority === 'medium' || alert.urgency === 'soon') return '#e97117';
  return '#2f8f52';
}

function urgencyLabel(alert) {
  if (alert.priority === 'high' || alert.urgency === 'urgent') return 'Agendar';
  if (alert.priority === 'medium' || alert.urgency === 'soon') return 'Ver oficinas';
  return 'OK';
}

function alertDescription(alert) {
  const parts = [];
  if (alert.trigger_km && alert.current_km) {
    const remaining = alert.trigger_km - alert.current_km;
    if (remaining > 0) parts.push(`Em ${remaining.toLocaleString('pt-BR')} km`);
    else parts.push('Km atingida');
  }
  if (alert.trigger_date) {
    const d = new Date(alert.trigger_date);
    const diff = Math.ceil((d - Date.now()) / 86400000);
    if (diff > 0) parts.push(`Vence em ${diff} dias`);
    else parts.push('Vencido');
  }
  if (alert.notes) parts.push(alert.notes);
  if (alert.interval_km) parts.push(`recomendado a cada ${alert.interval_km.toLocaleString('pt-BR')} km`);
  return parts.join(' · ') || 'Manutenção programada';
}

function vehicleEmoji(type) {
  const map = { car: '🚗', motorcycle: '🏍️', truck: '🛻', suv: '🚙' };
  return map[type] || '🚗';
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);
  const [tab, setTab] = useState('pending'); // 'pending' | 'read'
  const navigate = useNavigate();
  const m = useIsMobile();

  const loadAlerts = () => {
    alertsAPI.list()
      .then(r => {
        setAlerts(r.alerts || []);
        setNotifications(r.notifications || []);
      })
      .catch(() => toast.error('Erro ao carregar alertas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAlerts(); }, []);

  const handleDismiss = async (alertId) => {
    setDismissing(alertId);
    try {
      await alertsAPI.dismiss(alertId);
      // Atualiza localmente sem recarregar tudo
      setAlerts(prev => prev.map(a =>
        a.id === alertId ? { ...a, is_active: false, dismissed_at: new Date().toISOString() } : a
      ));
      toast.success('Alerta marcado como lido ✓');
    } catch (err) {
      toast.error(err.message || 'Erro ao marcar como lido');
    } finally {
      setDismissing(null);
    }
  };

  const handleReactivate = async (alertId) => {
    setDismissing(alertId);
    try {
      await alertsAPI.reactivate(alertId);
      setAlerts(prev => prev.map(a =>
        a.id === alertId ? { ...a, is_active: true, dismissed_at: null } : a
      ));
      toast.success('Alerta reativado');
    } catch (err) {
      toast.error(err.message || 'Erro ao reativar');
    } finally {
      setDismissing(null);
    }
  };

  const pendingAlerts = alerts.filter(a => a.is_active);
  const readAlerts = alerts.filter(a => !a.is_active);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '12px' }}>
      <div className="spinner" style={{ width: '32px', height: '32px' }} />
      <span style={{ fontSize: '13px', color: '#6a7268' }}>Carregando alertas...</span>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '4px' }}>
        <span style={A.eyebrow}>Fique atento</span>
        <h1 style={{ ...A.h1, fontSize: m ? '28px' : '44px' }}>Alertas</h1>
        <p style={A.subtitle}>
          {pendingAlerts.length === 0
            ? 'Nenhuma manutenção pendente. Tudo em dia! ✅'
            : `${pendingAlerts.length} manutenç${pendingAlerts.length === 1 ? 'ão chegando' : 'ões chegando'}. Não deixe passar.`
          }
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
        <button
          style={{ ...A.tab, ...(tab === 'pending' ? A.tabActive : {}) }}
          onClick={() => setTab('pending')}
        >
          Pendentes {pendingAlerts.length > 0 && <span style={{ background: tab === 'pending' ? 'rgba(255,255,255,.2)' : '#e97117', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '1px 6px', borderRadius: '999px', marginLeft: '6px' }}>{pendingAlerts.length}</span>}
        </button>
        <button
          style={{ ...A.tab, ...(tab === 'read' ? A.tabActive : {}) }}
          onClick={() => setTab('read')}
        >
          Lidos {readAlerts.length > 0 && <span style={{ background: tab === 'read' ? 'rgba(255,255,255,.2)' : '#999', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '1px 6px', borderRadius: '999px', marginLeft: '6px' }}>{readAlerts.length}</span>}
        </button>
      </div>

      {/* Pending tab */}
      {tab === 'pending' && (
        pendingAlerts.length === 0 ? (
          <div style={A.empty}>
            <div style={{ fontSize: '48px', opacity: 0.3, marginBottom: '16px' }}>🔔</div>
            <h3 style={{ ...A.title, fontSize: '18px', marginBottom: '8px' }}>Tudo em dia!</h3>
            <p style={A.desc}>Seus veículos estão com as manutenções em dia. Bom trabalho!</p>
          </div>
        ) : (
          <div style={A.list}>
            {pendingAlerts.map(alert => renderAlertCard(alert, false))}
          </div>
        )
      )}

      {/* Read tab */}
      {tab === 'read' && (
        readAlerts.length === 0 ? (
          <div style={A.empty}>
            <div style={{ fontSize: '48px', opacity: 0.3, marginBottom: '16px' }}>✅</div>
            <h3 style={{ ...A.title, fontSize: '18px', marginBottom: '8px' }}>Nenhum alerta lido</h3>
            <p style={A.desc}>Quando marcar alertas como lidos, eles aparecerão aqui.</p>
          </div>
        ) : (
          <div style={A.list}>
            {readAlerts.map(alert => renderAlertCard(alert, true))}
          </div>
        )
      )}

      {/* Notifications section */}
      {notifications.length > 0 && (
        <div style={{ marginTop: '48px' }}>
          <h2 style={{ ...A.title, fontSize: '20px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
            Notificações
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {notifications.map(n => (
              <div key={n.id} style={{
                padding: '14px 18px', borderRadius: '12px',
                background: n.is_read ? 'transparent' : '#fff',
                border: `1px solid ${n.is_read ? 'transparent' : '#e5ddc9'}`,
                display: 'flex', alignItems: 'center', gap: '12px',
                opacity: n.is_read ? 0.6 : 1,
              }}>
                {!n.is_read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#e97117', flexShrink: 0 }} />}
                <div style={{ flex: 1 }}>
                  <b style={{ fontSize: '14px', color: '#14562e', display: 'block' }}>{n.title || 'Notificação'}</b>
                  <small style={{ fontSize: '12.5px', color: '#6a7268' }}>{n.message}</small>
                </div>
                <small style={{ color: '#aaa', fontSize: '12px', flexShrink: 0 }}>
                  {n.created_at ? new Date(n.created_at).toLocaleDateString('pt-BR') : ''}
                </small>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  function renderAlertCard(alert, isRead) {
    const color = isRead ? '#bbb' : urgencyColor(alert);
    const label = urgencyLabel(alert);
    const isPrimary = !isRead && (alert.priority === 'high' || alert.urgency === 'urgent');
    const vehicleName = alert.nickname || alert.model || 'Veículo';
    const isLoading = dismissing === alert.id;

    return (
      <div
        key={alert.id}
        style={{
          ...A.card,
          padding: m ? '16px' : '20px 24px',
          alignItems: m ? 'flex-start' : 'center',
          flexDirection: m ? 'column' : 'row',
          opacity: isLoading ? 0.5 : (isRead ? 0.65 : 1),
        }}
        onMouseOver={e => { if (!isRead) { e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(15,64,35,.06)'; } }}
        onMouseOut={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
      >
        <div style={{ ...A.bar, background: color }} />
        <div style={A.info}>
          <h3 style={{ ...A.title, color: isRead ? '#999' : '#14562e' }}>
            {alert.service_name || alert.title || 'Manutenção'}
            {isRead && <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', marginLeft: '8px' }}>✓ Lido</span>}
          </h3>
          <p style={A.desc}>{alertDescription(alert)}</p>
        </div>
        <div style={A.right}>
          <span style={A.vehicleTag}>
            {vehicleEmoji(alert.vehicle_type)} {vehicleName}
          </span>
          {isRead ? (
            <button
              style={{ ...A.btn, background: 'transparent', color: '#14562e', border: '1.5px solid #e5ddc9', fontSize: '12px', padding: '8px 14px' }}
              onClick={() => handleReactivate(alert.id)}
              disabled={isLoading}
              onMouseOver={e => { e.currentTarget.style.borderColor = '#14562e'; e.currentTarget.style.background = '#e6f0e9'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = '#e5ddc9'; e.currentTarget.style.background = 'transparent'; }}
            >
              Reativar
            </button>
          ) : isPrimary ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                style={{ ...A.btn, ...A.btnPrimary }}
                onClick={() => navigate('/profissionais')}
                onMouseOver={e => { e.currentTarget.style.background = '#1a6b3a'; }}
                onMouseOut={e => { e.currentTarget.style.background = '#14562e'; }}
              >{label}</button>
              <button
                style={{ ...A.btn, background: '#f4ede0', color: '#6a7268', fontSize: '12px', padding: '8px 12px' }}
                onClick={() => handleDismiss(alert.id)}
                disabled={isLoading}
                title="Marcar como lido"
                onMouseOver={e => { e.currentTarget.style.background = '#ede4d1'; }}
                onMouseOut={e => { e.currentTarget.style.background = '#f4ede0'; }}
              >✓</button>
            </div>
          ) : (
            <button
              style={{ ...A.btn, background: '#e6f0e9', color: '#2f8f52' }}
              onClick={() => handleDismiss(alert.id)}
              disabled={isLoading}
              onMouseOver={e => { e.currentTarget.style.background = '#d4e8da'; }}
              onMouseOut={e => { e.currentTarget.style.background = '#e6f0e9'; }}
            >
              {isLoading ? '...' : 'OK ✓'}
            </button>
          )}
        </div>
      </div>
    );
  }
}
